import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import DialogActions from "@mui/material/DialogActions";
import LoadingButton from "@mui/lab/LoadingButton";
import moment from "moment";
import { BlobProvider } from "@react-pdf/renderer";
import PrintStrukKesimpulan from "../../components/PrintStrukKesimpulan/PrintStrukKesimpulan";
import PrintIcon from "@mui/icons-material/Print";
import useKesimpulan from "../../services/queries/useKesimpulan";

import {
    ContentUkm,
    ParamsKesimpulan,
    UserResponse,
} from "../../constants/types";
import { useAuthStore } from "../../stores/authStore";
import { Divider } from "@mui/material";
import { DatePickerFilter } from "../DatePickerFilter";

interface IModalPrintTutupBukuProps {
    user?: UserResponse["data"];
    ukmData?: ContentUkm;
    nowK: string;
    open: boolean;
    onClose: () => void;
}

const ModalPrintTutupBuku = ({
    user,
    ukmData,
    nowK,
    open,
    onClose,
}: IModalPrintTutupBukuProps) => {
    let now = moment().format("YYYY/MM/DD");
    const ukmIdUser = useAuthStore((s) => s.ukmIdUser);

    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isTabletScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

    const [state, setState] = useState({
        isBarangTerjual: false,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            [event.target.name]: event.target.checked,
        });
    };

    useEffect(() => {
        if (ukmData?.webBarangTerjualTutupBuku === 1) {
            setState((prev) => ({
                ...prev,
                isBarangTerjual: true,
            }));
        }
    }, [ukmData]);

    const initialParamsKesimpulan = {
        ukmId: undefined,
        tanggal: now,
        grupProduk: 1,
    };
    const [paramsKesimpulan, setParamsKesimpulan] = useState<ParamsKesimpulan>(
        initialParamsKesimpulan,
    );
    const { data: kesimpulan } = useKesimpulan(paramsKesimpulan);

    useEffect(() => {
        if (ukmIdUser) {
            setParamsKesimpulan((prev) => ({
                ...prev,
                ukmId: ukmIdUser && ukmIdUser[0],
            }));
        }
    }, [ukmIdUser]);

    return (
        <Dialog
            maxWidth="sm"
            fullScreen={isPhoneScreen}
            fullWidth
            open={open}
            onClose={onClose}
            scroll="body"
            PaperProps={{
                sx: {
                    borderRadius: 1,
                },
            }}
        >
            <DialogTitle>
                <Stack 
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Print Tutup Buku
                    </Typography>

                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />
            
            <DialogContent>
                <Stack mb={18}>
                    <Stack mb={2}>
                        <Typography mb={1}>Pilih tanggal</Typography>
                        <DatePickerFilter
                            selectedDate={moment(
                                paramsKesimpulan.tanggal,
                            ).toDate()}
                            onDateChange={(date) => {
                                if (date) {
                                    setParamsKesimpulan((prev) => ({
                                        ...prev,
                                        tanggal:
                                            moment(date).format("YYYY/MM/DD"),
                                    }));
                                }
                            }}
                        />
                    </Stack>
                    <FormControl
                        sx={{ px: 1 }}
                        component="fieldset"
                        variant="standard"
                    >
                        <FormLabel component="legend">
                            Pilih apakah ingin menyertakan "Barang Terjual" dalam print:
                        </FormLabel>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={state.isBarangTerjual}
                                        onChange={handleChange}
                                        name="isBarangTerjual"
                                    />
                                }
                                label="Sertakan Barang Terjual"
                            /> 
                        </FormGroup>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ padding: 2 }}>
                <BlobProvider
                    document={
                        <PrintStrukKesimpulan
                            namaToko={ukmData?.namaToko ?? "Toko"}
                            alamatToko={ukmData?.alamatToko ?? "Toko"}
                            telpToko={ukmData?.noTelp ?? "Toko"}
                            gambarToko={
                                ukmData?.gambarTokoBW === null
                                    ? undefined
                                    : ukmData?.gambarTokoBW
                            }
                            tanggalTransaksi={moment(
                                paramsKesimpulan.tanggal,
                            ).format("DD MMMM YYYY")}
                            html={undefined}
                            kesimpulan={kesimpulan ?? []}
                            isBarangTerjual={state.isBarangTerjual}
                        />
                    }
                >
                    {({ blob, url, loading, error }) => {
                        return (
                            <LoadingButton
                                disabled={
                                    user &&
                                    !user.akses.laporan.includes(36)
                                }
                                loading={loading}
                                startIcon={
                                    isPhoneScreen ||
                                    isTabletScreen ? undefined : (
                                        <PrintIcon />
                                    )
                                }
                                loadingPosition="center"
                                size="small"
                                variant="contained"
                                href={url ?? ""}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {/* Print Tutup Buku */}
                                {isPhoneScreen || isTabletScreen ? (
                                    <PrintIcon />
                                ) : (
                                    "Print Tutup Buku"
                                )}
                            </LoadingButton>
                        );
                    }}
                </BlobProvider>
            </DialogActions>
        </Dialog>
    );
};

export default ModalPrintTutupBuku;
